import React, { useEffect } from 'react';

import { Button, Divider, Group, Modal, Skeleton, Stack, Text, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import AthleteStatusBadge from 'components/status-badges/AthleteStatusBadge';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useUpdateAthleteStatus from 'hooks/athleteStatus/useUpdateAthleteStatus';
import { AthleteRetrieve, AthleteStatus } from 'types';
import notificationHandler from 'utils/notifications';

interface FormValues {
  startDate: Date | null;
  endDate: Date | null;
}

interface IEditStatusModalProps {
  opened: boolean;
  athlete: AthleteRetrieve;
  athleteStatus: AthleteStatus | null;
  previousStatus: AthleteStatus | null;
  followingStatus: AthleteStatus | null;
  closeHandler: () => void;
}

const EditStatusModal = ({
  opened,
  athlete,
  athleteStatus,
  previousStatus,
  followingStatus,
  closeHandler,
}: IEditStatusModalProps): JSX.Element => {
  const { dateFormat } = useLocalization();
  const { successNotification, errorNotification } = notificationHandler();

  const updateAthleteStatus = useUpdateAthleteStatus();

  const originalStartDate = athleteStatus?.startDate ? new Date(athleteStatus.startDate) : null;
  const originalEndDate = athleteStatus?.endDate ? new Date(athleteStatus.endDate) : null;
  const previousStatusStartDate = previousStatus?.startDate ? new Date(previousStatus.startDate) : null;
  const followingStatusEndDate = followingStatus?.endDate ? new Date(followingStatus.endDate) : null;

  const form = useForm<FormValues>({
    initialValues: {
      startDate: originalStartDate,
      endDate: originalEndDate,
    },
    validate: {
      startDate: (value, values) => {
        if (!value) {
          return 'Please select a start date';
        }
        if (value > new Date()) {
          return 'Please select an earlier start date';
        }
        if (values.endDate && value >= values.endDate) {
          return 'Please select a valid date range';
        }
        if (previousStatusStartDate && value <= previousStatusStartDate) {
          return 'Please select a later start date';
        }
        return null;
      },
      endDate: (value, values) => {
        if (originalEndDate && !value) {
          return 'Please select an end date';
        }
        if (value && values.startDate && value <= values.startDate) {
          return 'Please select a valid date range';
        }
        if (followingStatusEndDate && value && value >= followingStatusEndDate) {
          return 'Please select an earlier end date';
        }
        if (value && value > new Date()) {
          return 'Please select an earlier end date';
        }
        return null;
      },
    },
  });

  const handleCloseAndReset = (): void => {
    form.reset();
    closeHandler();
  };

  const handleSubmit = (values: FormValues): void => {
    if (athleteStatus && values.startDate) {
      updateAthleteStatus.mutate(
        {
          startDate: values.startDate.toISOString(),
          endDate: values.endDate?.toISOString() || null,
          athlete: athlete.athleteId,
          athleteStatusId: athleteStatus.athleteStatusId,
        },
        {
          onSuccess: () => {
            successNotification('Athlete status updated successfully');
            handleCloseAndReset();
          },
          onError: error => {
            errorNotification('Athlete status update failed', error.response?.data.message);
          },
        },
      );
    }
  };

  useEffect(() => {
    form.setFieldValue('startDate', originalStartDate);
    form.setFieldValue('endDate', originalEndDate);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteStatus]);

  return (
    <Modal
      opened={opened}
      onClose={handleCloseAndReset}
      size='md'
      centered
      title={<Title order={4}>Edit Status</Title>}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack p='md'>
          <Stack spacing='xs'>
            <Group noWrap>
              <Text fs='italic' c='dimmed' fz='sm' ta='right' w='100px'>
                Athlete Name
              </Text>
              <Text fz='sm' ta='left'>
                {athlete.fullName}
              </Text>
            </Group>
            <Group noWrap>
              <Text fs='italic' c='dimmed' fz='sm' ta='right' w='100px'>
                Status
              </Text>
              {athleteStatus ? (
                <Text fz='sm' ta='left'>
                  <AthleteStatusBadge status={athleteStatus.status} />
                </Text>
              ) : (
                <Skeleton h='sm' />
              )}
            </Group>
            <Group noWrap>
              <Text fs='italic' c='dimmed' fz='sm' ta='right' w='100px'>
                Date Range
              </Text>
              {athleteStatus ? (
                <Text fz='sm' ta='left'>
                  {`${dayjs(athleteStatus.startDate).format(dateFormat)} - ${
                    athleteStatus.endDate ? dayjs(athleteStatus.endDate).format(dateFormat) : ''
                  }`}
                </Text>
              ) : (
                <Skeleton h='sm' />
              )}
            </Group>
          </Stack>

          <Divider mx='-xl' />

          <Text color='dimmed' size='sm'>
            Changing the start date will lengthen or shorten the previous status. Changing the end date will
            lengthen or shorten the following status.
          </Text>

          <DatePickerInput
            {...form.getInputProps('startDate')}
            data-cy='startDateStatus'
            label='Start Date'
            required
            minDate={previousStatusStartDate || undefined}
            maxDate={form.values.endDate || followingStatusEndDate || new Date() || undefined}
            dropdownType='modal'
            modalProps={{ centered: true }}
          />
          <DatePickerInput
            {...form.getInputProps('endDate')}
            label='End Date'
            required
            minDate={form.values.startDate || previousStatusStartDate || undefined}
            maxDate={followingStatusEndDate || new Date() || undefined}
            disabled={!originalEndDate}
            dropdownType='modal'
            modalProps={{ centered: true }}
          />
        </Stack>

        <Group position='right' pt='md'>
          <Button variant='light' onClick={handleCloseAndReset} size='sm' radius='md'>
            Cancel
          </Button>
          <Button
            data-cy='editSaveStatus'
            type='submit'
            size='sm'
            radius='md'
            disabled={!athleteStatus}
            loading={updateAthleteStatus.isLoading}
          >
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default EditStatusModal;
